import {
  AccountAttributeTile,
  AccountAttributeTileType,
  AccountCopyAttributeTile,
  AccountProductEnum,
  AccountToggleAttributeTile,
  CardBalanceAttribute,
  ITile,
  Typenames,
} from '@varo/graphql-schema-types';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import HairLineDivider from 'shared/components/vxl/divider/HairlineDivider/HairLineDivider';
import ListItem from 'shared/components/vxl/ListItem/ListItem';
import { TagType } from 'shared/components/vxl/Tag/Tag';
import { VXLComponentId } from 'shared/renderer/types';
import theme from 'shared/styles/theme';

interface AccountAttributeTileProps {
  tile:
    | AccountCopyAttributeTile
    | AccountToggleAttributeTile
    | AccountAttributeTile;
  handleTilePress: (tile: ITile) => void;
  isLastTile: boolean;
  productType?: AccountProductEnum;
}

const Divider = ({ isLastTile }: { isLastTile: boolean }) =>
  !isLastTile ? (
    <View style={styles.dividerWrapper}>
      <HairLineDivider />
    </View>
  ) : null;

const AccountAttributeTileComponent = ({
  tile,
  handleTilePress,
  isLastTile,
  productType,
}: AccountAttributeTileProps) => {
  const { t: translatedText } = useTranslation('accounts', {
    keyPrefix: 'accountPageTiles',
  });

  const getPrimaryCopyColor = (copy: string | undefined) => {
    if (copy?.startsWith('+')) {
      return theme.color.status.positive;
    } else if (copy?.startsWith('-')) {
      return theme.color.status.negative;
    }
    return undefined; // No color if neither + nor -
  };

  const getTagCopy = (isOn: boolean) => {
    if (isOn) {
      return translatedText('tagStatus.on');
    }
    return translatedText('tagStatus.off');
  };

  switch (tile.__typename) {
    case Typenames.AccountCopyAttributeTile:
      return (
        <View>
          <ListItem
            onPress={
              productType !== AccountProductEnum.Advance
                ? () => handleTilePress(tile)
                : undefined
            }
            primaryCopy={(tile as AccountCopyAttributeTile)?.title}
            showsChevron={
              productType === AccountProductEnum.Advance
                ? Boolean((tile as { showsChevron?: boolean }).showsChevron)
                : Boolean(tile as { showsChevron?: boolean })
            }
            trailingAccessory={{
              id: '1',
              name: VXLComponentId.LIST_ITEM_TEXT_STACK,
              props: {
                primaryCopy: (tile as AccountCopyAttributeTile)?.copy || '',
                primaryCopyColor: getPrimaryCopyColor(
                  (tile as AccountCopyAttributeTile)?.copy || '',
                ),
                secondaryCopy: tile?.secondaryCopy,
              },
            }}
          />
          <Divider
            isLastTile={
              (tile as { showDivider?: boolean })?.showDivider ?? isLastTile
            }
          />
        </View>
      );
    case Typenames.AccountToggleAttributeTile:
      return (
        <View>
          <ListItem
            key={tile.id}
            onPress={() => handleTilePress(tile)}
            primaryCopy={(tile as AccountToggleAttributeTile)?.title}
            trailingAccessory={{
              id: 'account-toggle-switch',
              name: VXLComponentId.TAG,
              props: {
                onValueChange: () => handleTilePress(tile),
                type: tile.isOn ? TagType.SUCCESS : TagType.ERROR,
                text: getTagCopy(tile.isOn),
              },
            }}
          />
          <Divider isLastTile={isLastTile} />
        </View>
      );
    case Typenames.AccountAttributeTile:
      return (tile as AccountAttributeTile)?.accountAttributeTileType ===
        AccountAttributeTileType.CardBalance ? (
        <View>
          <ListItem
            key={tile.id}
            onPress={() => {
              handleTilePress(tile);
            }}
            primaryCopy={(tile as AccountAttributeTile)?.attribute?.title}
            trailingAccessory={{
              id: 'account-attribute-copy',
              name: VXLComponentId.LIST_ITEM_TEXT_STACK,
              props: {
                primaryCopy:
                  (
                    (tile as AccountAttributeTile)
                      ?.attribute as CardBalanceAttribute | undefined
                  )?.balance || '',
              },
            }}
            analyticsId="cardBalance"
            customAttributes={{
              account_type: 'charge_card',
              cta: 'Card balance',
            }}
          />
          <Divider isLastTile={isLastTile} />
        </View>
      ) : null;
    default:
      return <View key={tile.id} />;
  }
};

const styles = StyleSheet.create({
  dividerWrapper: {
    backgroundColor: theme.color.background.primary,
    paddingHorizontal: theme.spacing.m,
  },
});

export default AccountAttributeTileComponent;
